// src/components/sensors/sensorAiInsights.js
// FlowSense AI/ML for the sensor tables — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
// Combines the AI sources the platform already produces. Nothing is invented:
// a source that has no value returns null and the UI says why.
//
//   1. Page predictions   aiPredictions[assetId]   (useAiWeatherIntelligence:
//      floodRiskProbability, estimatedMinutesToOvertopping, predictedSeverity,
//      rateOfRisePerMinute, confidence, source 'server' | 'edge', serverStale)
//   2. Sensor record      sensor.ai_predictions    (GET /api/v1/sensors:
//      health_status / health_score / predicted_rul_days for the linked asset,
//      flood_probability / est_minutes_to_overtopping for water-level sensors,
//      *_source and *_reason when there is no prediction)
//   3. FlowSense edge checks, computed in the browser from the readings this
//      table actually received:
//        pressure  robust z-score (median / MAD) of the newest PSI against the
//                  previous readings — |z| ≥ 3.5 is a spike or a drop
//        level     a jump of two or more steps between consecutive readings,
//                  or ≥ 4 level changes in the last 10 readings (flapping)
// ═══════════════════════════════════════════════════════════════════════════

export const AI_FLAG_THRESHOLD = 0.65;          // same cut the AI FLOOD badge already used
export const AI_WATCH_THRESHOLD = 0.35;
export const ANOMALY_Z = 3.5;                   // Iglewicz & Hoaglin modified z-score cut-off
export const ANOMALY_MIN_POINTS = 8;
export const HEALTH_CRITICAL_SCORE = 40;        // same red cut the quality badges use
const LEVEL_ORDER = { EMPTY: 0, LOW: 1, MEDIUM: 2, HIGH: 3 };

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const median = (arr) => {
  const a = [...arr].sort((x, y) => x - y);
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

/**
 * Modified z-score of the newest value against the earlier ones.
 * @param {number[]} values oldest → newest
 * @returns {{ z:number, latest:number, baseline:number, spread:number, n:number } | null}
 */
export function robustZ(values, minPoints = ANOMALY_MIN_POINTS) {
  const v = (values || []).filter((x) => Number.isFinite(x));
  if (v.length < minPoints) return null;
  const latest = v[v.length - 1];
  const base = v.slice(0, -1);
  const med = median(base);
  const mad = median(base.map((x) => Math.abs(x - med)));
  let z;
  if (mad > 0) {
    z = (0.6745 * (latest - med)) / mad;
  } else {
    // Perfectly flat baseline: fall back to the mean absolute deviation; if that is 0
    // too, only a change larger than 0.5 PSI or 2 % counts (sensor noise floor).
    const meanAbs = base.reduce((s, x) => s + Math.abs(x - med), 0) / base.length;
    if (meanAbs > 0) z = (latest - med) / (1.253314 * meanAbs);
    else z = Math.abs(latest - med) > Math.max(0.5, Math.abs(med) * 0.02) ? Math.sign(latest - med) * 99 : 0;
  }
  return { z, latest, baseline: med, spread: mad, n: v.length };
}

/** Pressure spike / drop from real PSI history (oldest → newest). */
export function detectPressureAnomaly(values) {
  const r = robustZ(values);
  if (!r || Math.abs(r.z) < ANOMALY_Z) return null;
  const kind = r.z > 0 ? 'spike' : 'drop';
  return {
    kind,
    label: kind === 'spike' ? 'Pressure spike' : 'Pressure drop',
    severity: Math.abs(r.z) >= ANOMALY_Z * 2 ? 'critical' : 'warning',
    z: r.z,
    detail: `${r.latest.toFixed(1)} PSI vs typical ${r.baseline.toFixed(1)} PSI over the last ${r.n - 1} readings`
      + (Math.abs(r.z) >= 99 ? '' : ` (z = ${r.z.toFixed(1)})`),
    method: `Robust z-score (median/MAD), flagged at |z| ≥ ${ANOMALY_Z}`,
  };
}

/** Level jump / flapping from real level history (NEWEST FIRST, as sensorHistory stores it). */
export function detectLevelAnomaly(historyNewestFirst) {
  const h = (historyNewestFirst || []).filter((e) => e && LEVEL_ORDER[String(e.level).toUpperCase()] !== undefined);
  if (h.length < 2) return null;
  const a = LEVEL_ORDER[String(h[0].level).toUpperCase()];
  const b = LEVEL_ORDER[String(h[1].level).toUpperCase()];
  if (Math.abs(a - b) >= 2) {
    const rising = a > b;
    return {
      kind: rising ? 'rapid_rise' : 'rapid_fall',
      label: rising ? 'Rapid rise' : 'Rapid fall',
      severity: rising ? 'critical' : 'warning',
      detail: `${String(h[1].level).toUpperCase()} → ${String(h[0].level).toUpperCase()} between consecutive readings`,
      method: 'Level jumped two or more steps between consecutive readings',
    };
  }
  const last10 = h.slice(0, 10);
  let changes = 0;
  for (let i = 1; i < last10.length; i += 1) if (last10[i].level !== last10[i - 1].level) changes += 1;
  if (last10.length >= 6 && changes >= 4) {
    return {
      kind: 'flapping',
      label: 'Flapping',
      severity: 'warning',
      detail: `${changes} level changes in the last ${last10.length} readings — float switch bouncing or wave action`,
      method: '4 or more level changes within the last 10 readings',
    };
  }
  return null;
}

/** Flood prediction from the page model first, then the sensor record. */
function floodFrom(prediction, ai) {
  const p = prediction || null;
  const pageProb = num(p?.floodRiskProbability ?? p?.flood_probability);
  if (pageProb !== null) {
    return {
      probability: pageProb,
      minutesToOvertop: num(p.estimatedMinutesToOvertopping ?? p.est_minutes_to_overtopping),
      severity: p.predictedSeverity || p.flood_risk || null,
      ratePerMin: num(p.rateOfRisePerMinute ?? p.rate_of_rise_per_minute),
      confidence: num(p.confidence),
      trend: p.trend || null,
      source: p.source === 'edge' ? 'FlowSense edge model' : 'FlowSense ML service',
      stale: !!p.serverStale,
    };
  }
  const recProb = num(ai?.flood_probability);
  if (recProb !== null) {
    return {
      probability: recProb,
      minutesToOvertop: num(ai.est_minutes_to_overtopping),
      severity: ai.flood_risk || null,
      ratePerMin: num(ai.rate_of_rise_per_minute),
      confidence: num(ai.flood_confidence),
      trend: null,
      source: 'FlowSense ML service',
      stale: false,
    };
  }
  return null;
}

function healthFrom(ai) {
  if (!ai) return null;
  const score = num(ai.health_score);
  const rul = num(ai.predicted_rul_days);
  const status = ai.health_status || null;
  if (score === null && rul === null && !status) return null;
  const critical = (status && /crit|poor|fail|severe|replace/i.test(status)) || (score !== null && score < HEALTH_CRITICAL_SCORE);
  const watch = !critical && ((status && /fair|warn|degrad|watch/i.test(status)) || (score !== null && score < 70));
  return { score, rulDays: rul, status, level: critical ? 'critical' : watch ? 'warning' : 'ok' };
}

const SEVERITY_RANK = { critical: 3, warning: 2, ok: 1, none: 0 };

/**
 * One AI view-model per row.
 * @param {object} p
 * @param {'pressure'|'water_level'} p.kind
 * @param {object} p.sensor
 * @param {object|null} p.prediction   aiPredictions[assetId]
 * @param {number[]}   [p.psiValues]   oldest → newest (pressure)
 * @param {object[]}   [p.levelHistory] newest first (water level)
 */
export function deriveSensorAi({ kind, sensor, prediction, psiValues, levelHistory }) {
  const ai = sensor?.ai_predictions || null;
  const flood = kind === 'water_level' ? floodFrom(prediction, ai) : null;
  const health = healthFrom(ai);
  const anomaly = kind === 'water_level' ? detectLevelAnomaly(levelHistory) : detectPressureAnomaly(psiValues);

  const reasons = [];
  if (kind === 'water_level' && !flood) {
    if (ai?.flood_reason) reasons.push(ai.flood_reason);
    else if (!(sensor?.linked_entity_id || sensor?.asset_id)) reasons.push('Bind this sensor to a well/tank asset to enable flood forecasting.');
    else reasons.push('No flood prediction for the linked asset yet.');
  }
  if (!health && ai?.health_reason && kind === 'pressure') reasons.push(ai.health_reason);
  if (!anomaly) {
    const have = kind === 'water_level' ? (levelHistory || []).length : (psiValues || []).length;
    const need = kind === 'water_level' ? 2 : ANOMALY_MIN_POINTS;
    if (have < need) reasons.push(`Edge anomaly check needs ${need} readings (${have} so far).`);
  }

  let level = 'none';
  const bump = (l) => { if (SEVERITY_RANK[l] > SEVERITY_RANK[level]) level = l; };
  if (flood) bump(flood.probability >= AI_FLAG_THRESHOLD ? 'critical' : flood.probability >= AI_WATCH_THRESHOLD ? 'warning' : 'ok');
  if (health) bump(health.level);
  if (anomaly) bump(anomaly.severity);
  if (level === 'none' && !anomaly && ((psiValues || []).length >= ANOMALY_MIN_POINTS || (levelHistory || []).length >= 2)) bump('ok');

  // Sort key: flood probability when known, otherwise severity rank / 4.
  const score = flood ? flood.probability : SEVERITY_RANK[level] / 4;
  const flagged = level === 'critical' || (flood ? flood.probability >= AI_FLAG_THRESHOLD : false) || !!anomaly;
  return { flood, health, anomaly, level, score, flagged, reasons, hasAny: !!(flood || health || anomaly) };
}

export const AI_LEVEL_META = Object.freeze({
  critical: { color: 'red', label: 'High risk' },
  warning: { color: 'orange', label: 'Watch' },
  ok: { color: 'green', label: 'Normal' },
  none: { color: 'gray', label: 'No AI data' },
});
